import { ApolloClient, InMemoryCache, from, ApolloLink, createHttpLink } from '@apollo/client';
import type { Operation, NextLink } from '@apollo/client';
import { createAuthErrorLink } from '@auth/apollo/authErrorLink';
import { logger } from '@core/logging';
import { createLoggingCorrelationIdLink, createLoggingErrorLink } from '@core/logging/apolloIntegration';

import { config } from '../../config';

// GraphQL endpoint URL from centralized configuration
const GRAPHQL_ENDPOINT = config.api.graphqlUrl;

// Create logging links for comprehensive GraphQL operation tracking
const loggingCorrelationIdLink = createLoggingCorrelationIdLink();
const loggingErrorLink = createLoggingErrorLink();

// Create an HTTP link for GraphQL operations
const httpLink = createHttpLink({
  uri: GRAPHQL_ENDPOINT,
  credentials: 'include', // Include cookies for authentication
});

// Create an auth error link to handle authentication errors
const authErrorLink = createAuthErrorLink();

// Create a link that injects the logger into the Apollo context
const loggerContextLink = new ApolloLink((operation: Operation, forward: NextLink) => {
  operation.setContext(({ ...context }) => ({
    ...context,
    logger,
  }));

  return forward(operation);
});

// Create the Apollo Client instance
export const apolloClient = new ApolloClient({
  link: from([loggerContextLink, loggingCorrelationIdLink, authErrorLink, loggingErrorLink, httpLink]),
  cache: new InMemoryCache({
    typePolicies: {
      // Core audit entities - ensure proper normalization by ID
      AuditSession: {
        keyFields: ['id'],
      },
      AuditItem: {
        keyFields: ['id'],
      },
      AuditDecision: {
        keyFields: ['id'],
      },
      // CRITICAL: Prevent individual normalization of decision responses
      // This ensures decisions stay within their query/mutation context
      AuditDecisionResponse: {
        keyFields: false, // Don't normalize individually - keep in context
      },
      UpsertAuditDecisionResponse: {
        keyFields: false, // Don't normalize individually - keep in context
      },
      Community: {
        keyFields: ['id'],
        fields: {
          auditSession: {
            keyArgs: ['sessionId'],
          },
        },
      },
      AuditReport: {
        keyFields: ['id'],
      },

      // Query-level cache policies for optimal 4-query architecture performance
      Query: {
        fields: {
          // Audit session statistics - cache by session ID for reactive updates
          auditSessionStats: {
            keyArgs: ['auditSessionId'],
          },

          // Audit items - cache by session ID and pagination parameters
          auditItems: {
            keyArgs: ['input', ['auditSessionId', 'page', 'pageSize', 'status', 'label', 'severity']],
          },

          // Audit decisions - cache by session ID for consistent access
          auditDecisions: {
            keyArgs: ['input', ['auditSessionId']],
          },
        },
      },

      // Pagination result types - merge policies for efficient list updates
      AuditItemsResponse: {
        fields: {
          items: {
            merge(_existing = [], incoming) {
              return incoming;
            },
          },
        },
      },

      AuditDecisionsResponse: {
        fields: {
          decisions: {
            merge(_existing = [], incoming) {
              return incoming;
            },
          },
        },
      },
    },
  }),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
    },
    query: {
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    },
    mutate: {
      errorPolicy: 'none',
    },
  },
});

/**
 * Creates an Apollo Client instance with logger context
 * This function should be used when the logger is available from React context
 * @param loggerInstance The logger instance from React context
 * @returns Configured Apollo Client with logger context
 */
